import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, Edit, MoreVertical, Trash, Copy, Users, Share, ExternalLink } from 'lucide-react';
import { MusicIcon } from 'lucide-react';
import { generatePrintUrl } from '../utils/PrintSettingsUtils';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Reusable Setlist Card Component
 * Standard actions available everywhere: View/Edit, Share, Open All
 * Context-specific actions: Rename (for owners), Delete (for owners), Remove from Band (band context)
 * 
 * @param {Object} setlist - The setlist object to display
 * @param {Function} onRename - Callback when rename is clicked (for inline renaming)
 * @param {Function} onDelete - Callback when delete is clicked
 * @param {Function} onRemoveFromBand - Callback when remove from band is clicked (band context)
 * @param {Function} onDuplicate - Callback when duplicate is clicked
 * @param {boolean} canManage - Whether the user can manage this setlist (owner or band admin)
 * @param {string} context - Context where card is used ('band', 'profile', 'setlists')
 */
const SetlistCard = ({ 
  setlist, 
  onRename,
  onDelete,
  onRemoveFromBand,
  onDuplicate,
  canManage = false,
  context = 'setlists'
}) => {
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const handleCardClick = () => {
    navigate(`/setlist/${setlist._id}`);
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/setlist/${setlist._id}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        showNotification('Setlist link copied to clipboard!', 'success');
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
        showNotification('Failed to copy link', 'error');
      });
  };

  const handleOpenAll = () => {
    if (setlist.sheets && setlist.sheets.length > 0) {
      setlist.sheets.forEach((sheet, index) => {
        setTimeout(() => {
          const sheetUrl = generatePrintUrl(sheet._id);
          window.open(sheetUrl, `_sheet_${sheet._id}`);
        }, 100 * index);
      });
      showNotification(`Opening ${setlist.sheets.length} sheets in print preview`, 'success');
    } else {
      showNotification('No sheets in this setlist', 'warning');
    }
  };

  return (
    <div 
      className="border border-border rounded-md p-4 bg-card hover:bg-card/80 transition-colors cursor-pointer relative"
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center overflow-hidden">
          <MusicIcon className="h-5 w-5 mr-2 flex-shrink-0 text-muted-foreground" />
          <h3 className="text-lg font-medium truncate">{setlist.name || 'Untitled Setlist'}</h3>
        </div>
        
        {/* Always show dropdown menu */}
        <div 
          className="relative" 
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowDropdown(!showDropdown);
            }}
            className="p-1 rounded-md hover:bg-background flex-shrink-0"
          >
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-border">
              {/* Standard actions available to everyone */}
              <button 
                className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(false);
                  navigate(`/setlist/${setlist._id}`);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                View/Edit
              </button>

              <button 
                className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(false);
                  handleShare();
                }}
              >
                <Share className="h-4 w-4 mr-2" />
                Share
              </button>

              <button 
                className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDropdown(false);
                  handleOpenAll();
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open All Sheets
              </button>

              {/* Owner-only actions */}
              {canManage && onRename && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onRename(setlist);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Rename
                </button>
              )}

              {canManage && onDuplicate && (
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    onDuplicate(setlist);
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </button>
              )}

              {/* Context-specific delete actions */}
              {context === 'band' && canManage && onRemoveFromBand && (
                <>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button 
                    className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      onRemoveFromBand(setlist._id);
                    }}
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Remove from Band
                  </button>
                </>
              )}
              
              {context !== 'band' && canManage && onDelete && (
                <>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button 
                    className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(false);
                      onDelete(setlist);
                    }}
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Description with HTML rendering - always show */}
      {setlist.description && (
        <div 
          className="text-sm text-muted-foreground mb-2 line-clamp-2 [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
          dangerouslySetInnerHTML={{ __html: setlist.description }}
        />
      )}

      {/* Date and sheet count - always show */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center">
          <CalendarIcon className="w-4 h-4 mr-1" />
          <span>{formatDate(setlist.createdAt || setlist.created_at)}</span>
        </div>
        <div className="flex items-center gap-3">
          {setlist.band_id && (
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              <span>Band</span>
            </div>
          )}
          <div>
            {setlist.sheets?.length || 0} sheets
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetlistCard;